/**
 * Starter FilterChip — pill button used for filter rails (e.g. the
 * projects-table sticky header). Inactive: transparent fill, border-default,
 * fg-secondary label. Active reads as the brand: selected-bg fill +
 * selected-fg label + transparent border (active-hover deepens to the brand
 * tint). Disabled gets a distinct muted treatment so it can't be confused
 * with an interactive inactive chip. Optional count badge on the right.
 *
 * Pure CSS-Module-only — no Mantine functional layer. Renders a `<button>`
 * so it's always keyboard-accessible.
 */
import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import styles from './FilterChip.module.css';

export interface FilterChipProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Active = brand selected-bg fill + selected-fg text. */
  readonly active?: boolean;
  /** Optional count badge rendered to the right of the label. */
  readonly count?: number;
  readonly children: ReactNode;
}

export const FilterChip = forwardRef<HTMLButtonElement, FilterChipProps>(
  function FilterChip(
    { active = false, count, className, children, type = 'button', ...rest },
    ref,
  ) {
    const classes = [styles.root, active ? styles.active : undefined, className]
      .filter(Boolean)
      .join(' ');
    return (
      <button ref={ref} type={type} className={classes} {...rest}>
        <span className={styles.label}>{children}</span>
        {count !== undefined ? <span className={styles.count}>{count}</span> : null}
      </button>
    );
  },
);
